// src/components/LeagueCalendarEditor.js
// The league calendar (founder 2026-08-18): the exact day + time of EVERY
// round/race. A rhythm pattern fills the rows; every row stays editable by
// hand. Shared by CreateLeagueScreen (posting) and LeagueCommandCenterScreen
// (commissioner edits until lock). Pure/controlled — the parent owns
// `roundTimes` (ms[], index = round-1) and `pattern`.
//
// Validation mirrors functions/modules/leagues.js validateRoundTimes:
// increasing order, spacing ≥ format duration (5-min floor), round 1 at
// least an hour out (skipped when round 1 is unchanged from the saved
// league — a Command Center save minutes before lock must still go through).
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  WEEKDAYS, PATTERN_KINDS, LEAGUE_MIN_LEAD_MS,
  generateRoundTimes, roundTimeIssues, fmtWhen, fmtDay, fmtSpan, describeCalendar,
} from '../utils/leagueSchedule';
import useCritsEnabled from '../hooks/useCritsEnabled';

let DatePicker = null;
try {
  DatePicker = require('../utils/DatePicker.web.js').default;
} catch (_) {
  DatePicker = null;
}

const GOLD = '#FCD000';
const CYAN = '#4FC3F7';
const CALENDAR_PREVIEW_ROWS = 10;

function Pill({ label, active, onPress, disabled, small }) {
  return (
    <TouchableOpacity onPress={onPress} disabled={disabled}
      style={[st.pill, small && st.pillSmall, active && st.pillActive, disabled && st.pillDisabled]}>
      <Text style={[st.pillText, small && st.pillTextSmall, active && st.pillTextActive]}>{label}</Text>
    </TouchableOpacity>
  );
}

/**
 * Props:
 *  roundTimes        ms[]            — the calendar (parent state)
 *  onChange(times)                   — full replacement
 *  pattern / onPattern               — { kind, weekdays, everyN }
 *  totalRounds       number          — expected row count (parent resizes roundTimes)
 *  roundNoun         'round'|'race'|'match'
 *  durationMinutes   number          — format duration (spacing floor)
 *  editable          bool
 *  originalFirstMs   number|null     — saved round-1 start (lead check skipped when unchanged)
 *  seasonEnd         ISO string|null — platform season end (informational)
 *  gamesPerEntrant   number|null     — h2h summary clause (null for race/BR: rounds = games)
 *  minLeadMs         number          — round-1 lead floor (admins mirror the server's 2-minute lead)
 *  entrantNoun       'entrant'|'team'
 *  onValidity(ok)                    — reports whether the calendar is postable
 */
export default function LeagueCalendarEditor({
  roundTimes, onChange, pattern, onPattern, totalRounds, roundNoun = 'round',
  durationMinutes = 30, editable = true, originalFirstMs = null, seasonEnd = null,
  gamesPerEntrant, entrantNoun = 'entrant', onValidity, minLeadMs = LEAGUE_MIN_LEAD_MS,
}) {
  const RoundNoun = roundNoun.charAt(0).toUpperCase() + roundNoun.slice(1);
  const plural = (n) => `${roundNoun}${n === 1 ? '' : (roundNoun === 'match' ? 'es' : 's')}`;
  const [showAll, setShowAll] = useState(false);
  // The league Crit pool is named only while the Crit economy is on
  // (settings/economy.critsEnabled; same gate as LeagueDetailScreen's season note).
  const critsOn = useCritsEnabled();

  // Re-check the lead-time rule every minute (a form left open drifts past
  // the "1 hour from now" floor).
  const [tick, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick((t) => t + 1), 60000); return () => clearInterval(id); }, []);
  const issues = useMemo(() => {
    const raw = roundTimeIssues(roundTimes, {
      durationMinutes, nowMs: Date.now(), minLeadMs, noun: roundNoun,
    });
    // Unchanged round 1 on a saved league is never "too soon".
    if (originalFirstMs != null && roundTimes[0] === originalFirstMs && raw[0]) raw[0] = null;
    return raw;
  }, [roundTimes, durationMinutes, roundNoun, originalFirstMs, minLeadMs, tick]);
  const ok = roundTimes.length === totalRounds && issues.every((x) => !x);
  useEffect(() => { onValidity?.(ok); }, [ok]); // eslint-disable-line react-hooks/exhaustive-deps

  const startMs = roundTimes[0];
  const lastMs = roundTimes[roundTimes.length - 1];
  const projectedEndMs = Number.isFinite(lastMs) ? lastMs + durationMinutes * 60000 : NaN;
  const seasonEndMs = seasonEnd ? new Date(seasonEnd).getTime() : null;
  const crossesSeason = !!seasonEndMs && Number.isFinite(projectedEndMs) && projectedEndMs >= seasonEndMs;
  const rhythm = describeCalendar(roundTimes);

  const setRow = (i, ms) => { const next = [...roundTimes]; next[i] = ms; onChange(next); };
  const applyPattern = () => onChange(generateRoundTimes(roundTimes[0], totalRounds, pattern));
  const toggleWeekday = (d) => {
    const has = (pattern.weekdays || []).includes(d);
    const weekdays = has ? pattern.weekdays.filter((x) => x !== d) : [...(pattern.weekdays || []), d].sort((a, b) => a - b);
    onPattern({ ...pattern, kind: 'weekly', weekdays });
  };

  const pickerStyle = {
    backgroundColor: '#0f1115', color: GOLD, borderColor: '#2a2f36',
    borderWidth: 1, borderRadius: 8, padding: 8, height: 38, fontSize: 13,
  };
  const renderPicker = (i) => (editable ? (DatePicker ? (
    <DatePicker
      value={new Date(roundTimes[i])}
      onChange={(d) => setRow(i, d.getTime())}
      style={pickerStyle}
    />
  ) : (
    <TextInput
      style={st.input}
      value={new Date(roundTimes[i]).toISOString().slice(0, 16)}
      onChangeText={(v) => { const d = new Date(v); if (!Number.isNaN(d.getTime())) setRow(i, d.getTime()); }}
      placeholder="YYYY-MM-DDTHH:MM"
      placeholderTextColor="#666"
    />
  )) : (
    <Text style={st.readonlyWhen}>{fmtWhen(roundTimes[i])}</Text>
  ));
  const visibleRows = showAll ? roundTimes.length : Math.min(roundTimes.length, CALENDAR_PREVIEW_ROWS);

  return (
    <View>
      {editable && (
        <>
          <Text style={st.label}>Rhythm</Text>
          <View style={st.pillRow}>
            {PATTERN_KINDS.map((k) => (
              <Pill key={k.key} label={k.label} active={pattern.kind === k.key}
                onPress={() => onPattern(pattern.kind === k.key ? pattern
                  : { ...pattern, kind: k.key, everyN: k.key === 'everyMin' ? 45 : 2 })} />
            ))}
          </View>
          {pattern.kind === 'weekly' && (
            <View style={[st.pillRow, { marginTop: 8, alignItems: 'center' }]}>
              {WEEKDAYS.map((d, i) => (
                <Pill key={d} label={d} small active={(pattern.weekdays || []).includes(i)} onPress={() => toggleWeekday(i)} />
              ))}
              <Text style={[st.note, { marginTop: 0, marginLeft: 4 }]}>
                {(pattern.weekdays || []).length
                  ? `on these days at ${roundNoun} 1's time — if ${roundNoun} 1 isn't on one of them, Fill moves it to the first that is`
                  : `same weekday as ${roundNoun} 1, at ${roundNoun} 1's time`}
              </Text>
            </View>
          )}
          {(pattern.kind === 'everyDays' || pattern.kind === 'everyMin') && (
            <View style={[st.row, { marginTop: 8 }]}>
              <Text style={[st.note, { marginTop: 0, marginRight: 8 }]}>Every</Text>
              <TextInput
                style={st.numInput}
                value={String(pattern.everyN ?? '')}
                onChangeText={(v) => onPattern({ ...pattern, everyN: v.replace(/[^0-9]/g, '') })}
                onBlur={() => onPattern({ ...pattern, everyN: Math.max(pattern.kind === 'everyMin' ? 5 : 1, Math.floor(Number(pattern.everyN) || 1)) })}
                keyboardType="numeric"
                maxLength={4}
              />
              <Text style={[st.note, { marginTop: 0, marginLeft: 8 }]}>{pattern.kind === 'everyMin' ? 'minutes (same-day sprint)' : 'days'}</Text>
            </View>
          )}
          <View style={st.row}>
            <TouchableOpacity style={st.applyBtn} onPress={applyPattern}>
              <Ionicons name="calendar" size={14} color="#000" style={{ marginRight: 6 }} />
              <Text style={st.applyBtnText}>Fill all {totalRounds} {plural(totalRounds)} from the rhythm</Text>
            </TouchableOpacity>
          </View>
          <Text style={st.note}>
            Pick a rhythm and press Fill to write the rows, then change any row below by hand.
            {' '}{RoundNoun} 1 is the league start — registration closes 15 minutes before it.
          </Text>
        </>
      )}

      {/* The calendar */}
      <View style={st.calendar}>
        {roundTimes.slice(0, visibleRows).map((ms, i) => (
          <View key={i} style={[st.calRow, issues[i] && st.calRowBad]}>
            <View style={st.calLabelWrap}>
              <Text style={st.calLabel}>{RoundNoun} {i + 1}</Text>
              {i === totalRounds - 1 && totalRounds > 1 && <Text style={st.calFinal}>Final</Text>}
            </View>
            <View style={{ flex: 1 }}>
              {renderPicker(i)}
              {!!issues[i] && <Text style={st.calIssue}>{issues[i]}</Text>}
            </View>
          </View>
        ))}
        {roundTimes.length > CALENDAR_PREVIEW_ROWS && (
          <TouchableOpacity onPress={() => setShowAll((v) => !v)} style={st.showAllBtn}>
            <Text style={st.showAllText}>
              {showAll ? 'Show fewer' : `Show all ${roundTimes.length} ${plural(roundTimes.length)}`}
              {!showAll && issues.slice(CALENDAR_PREVIEW_ROWS).some(Boolean) ? '  ⚠ a later date needs a look' : ''}
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Summary */}
      <View style={[st.infoBox, !ok && st.infoBoxBad]}>
        <Text style={[st.infoTitle, !ok && { color: '#ff6b6b' }]}>
          {totalRounds} {plural(totalRounds)}
          {gamesPerEntrant != null && roundNoun === 'round' ? ` · each ${entrantNoun} plays ${gamesPerEntrant} ${gamesPerEntrant === 1 ? 'game' : 'games'}` : ''}
          {' '}· about {fmtSpan(projectedEndMs - startMs)}
        </Text>
        <Text style={st.infoRow}>{rhythm ? `${rhythm}  ·  ` : ''}{fmtDay(startMs)} → {fmtDay(lastMs)}</Text>
        <Text style={st.infoRow}>{RoundNoun} 1 — {fmtWhen(startMs)}</Text>
        {totalRounds > 1 && <Text style={st.infoRow}>Final ({RoundNoun} {totalRounds}) — {fmtWhen(lastMs)}</Text>}
        {!!seasonEnd && (
          <Text style={[st.infoRow, crossesSeason ? { color: '#f5b342', fontWeight: '800' } : { color: '#8a8a8a' }]}>
            {crossesSeason
              ? (critsOn
                ? `Runs past the current CritCash season end (${new Date(seasonEnd).toLocaleDateString([], { month: 'long', day: 'numeric' })}). That's fine — the league keeps going and its Crit pool stays with the league; you'll hear from us before a season changes.`
                : `Runs past the current CritCash season end (${new Date(seasonEnd).toLocaleDateString([], { month: 'long', day: 'numeric' })}). That's fine: the league keeps going, and you'll hear from us before a season changes.`)
              : `Finishes inside the current CritCash season (ends ${new Date(seasonEnd).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })}).`}
          </Text>
        )}
        {!ok && editable && (
          <Text style={[st.infoRow, { color: '#ff6b6b' }]}>
            {roundTimes.length !== totalRounds
              ? `The calendar has ${roundTimes.length} rows but the settings need ${totalRounds}.`
              : `Fix the highlighted ${plural(issues.filter(Boolean).length)} above.`}
          </Text>
        )}
      </View>
    </View>
  );
}

const st = StyleSheet.create({
  label: { color: '#ddd', fontSize: 13, fontWeight: '800', marginTop: 14, marginBottom: 6 },
  note: { color: '#8a8a8a', fontSize: 11, marginTop: 6, lineHeight: 15 },
  row: { flexDirection: 'row', alignItems: 'center', marginTop: 8, flexWrap: 'wrap' },
  pillRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  pill: {
    borderWidth: 1, borderColor: '#2a2a2a', backgroundColor: '#151515',
    paddingVertical: 7, paddingHorizontal: 12, borderRadius: 10,
  },
  pillSmall: { paddingVertical: 5, paddingHorizontal: 9, borderRadius: 8 },
  pillActive: { backgroundColor: GOLD, borderColor: GOLD },
  pillDisabled: { opacity: 0.4 },
  pillText: { color: '#ddd', fontWeight: '800', fontSize: 12 },
  pillTextSmall: { fontSize: 11 },
  pillTextActive: { color: '#000' },
  input: {
    backgroundColor: '#0f1115', borderColor: '#2a2f36', borderWidth: 1,
    borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10,
    color: '#fff', fontSize: 14, marginTop: 2,
  },
  numInput: {
    width: 64, height: 36, textAlign: 'center',
    backgroundColor: '#0f1115', borderColor: '#2a2f36', borderWidth: 1, borderRadius: 8,
    color: GOLD, fontSize: 15, fontWeight: '900',
  },
  applyBtn: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: GOLD,
    borderRadius: 8, paddingVertical: 8, paddingHorizontal: 12, marginTop: 4,
  },
  applyBtnText: { color: '#000', fontWeight: '900', fontSize: 12 },
  calendar: { marginTop: 10, borderRadius: 10, borderWidth: 1, borderColor: '#20242c', overflow: 'hidden' },
  calRow: {
    flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 8, paddingHorizontal: 10,
    borderBottomWidth: 1, borderBottomColor: '#181b21', backgroundColor: '#0d0f13',
  },
  calRowBad: { backgroundColor: '#1a1012', borderLeftWidth: 3, borderLeftColor: '#ff6b6b' },
  calLabelWrap: { width: 84, paddingTop: 9 },
  calLabel: { color: '#fff', fontWeight: '900', fontSize: 12 },
  calFinal: { color: GOLD, fontSize: 10, fontWeight: '800', marginTop: 1 },
  calIssue: { color: '#ff6b6b', fontSize: 11, marginTop: 4, fontWeight: '700' },
  readonlyWhen: { color: GOLD, fontSize: 13, fontWeight: '800', paddingTop: 9 },
  showAllBtn: { paddingVertical: 10, alignItems: 'center', backgroundColor: '#0f1115' },
  showAllText: { color: CYAN, fontWeight: '800', fontSize: 12 },
  infoBox: {
    backgroundColor: '#0d0f13', borderLeftWidth: 3, borderLeftColor: GOLD,
    borderRadius: 8, padding: 10, marginTop: 10,
  },
  infoBoxBad: { borderLeftColor: '#ff6b6b' },
  infoTitle: { color: GOLD, fontSize: 12, fontWeight: '900', marginBottom: 6 },
  infoRow: { color: '#cfcfcf', fontSize: 12, marginBottom: 3 },
});
